/**
 * @file: ReservationAssociationSheet.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * A bottom panel listing the open reservations for the entered plate, with
 * enough detail (expected arrival, advisor, business type, booked items,
 * customer) to tell apart multiple bookings — a vehicle may have several
 * reservations at different times for different purposes. Tap a card to select
 * it (a check appears); confirm with the bottom button to link it to the order
 * being created (the backend converts it on order placement).
 */

import { Typography } from '@euler/app/components/typography';
import { LD } from '@euler/app/flows/leads/pages/detail/components/leadDetailKit';
import { BottomPanel } from '@euler/components/BottomPanel';
import { StatusPill, StatusTone } from '@euler/components/StatusPill';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  Reservation,
  ReservationStatus,
  ReservationStatusLabel,
} from '@euler/model/reservation/Reservation';
import { dateFromValue } from '@euler/utils/datetime';
import format from 'date-fns/format';
import { CheckCircle2 } from 'lucide-react-native';
import { FC, memo, useEffect, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';

const toneFor = (status: ReservationStatus): StatusTone =>
  status === 'arrived' ? 'active' : status === 'confirmed' ? 'info' : 'neutral';

const formatArrival = (value: string | Date | undefined | null): string => {
  if (!value) return '待定';
  try {
    return format(dateFromValue(value), 'MM-dd HH:mm');
  } catch {
    return '待定';
  }
};

const tagsOf = (tags: string | undefined | null): string =>
  tags
    ? tags
        .split(',')
        .map(t => t.trim())
        .filter(Boolean)
        .join(' · ')
    : '';

const DetailRow: FC<{ label: string; value: string }> = ({ label, value }) => {
  const { rpx } = useResponsiveContext();
  if (!value) return null;
  return (
    <View style={{ flexDirection: 'row', marginTop: rpx(4) }}>
      <Typography.Text
        size={rpx(12)}
        color={LD.steel}
        style={{ width: rpx(60) }}
      >
        {label}
      </Typography.Text>
      <Typography.Text
        size={rpx(12)}
        color={LD.ink}
        medium
        style={{ flex: 1 }}
        numberOfLines={2}
      >
        {value}
      </Typography.Text>
    </View>
  );
};

const ReservationCard: FC<{
  reservation: Reservation;
  selected: boolean;
  onPress: () => void;
}> = memo(({ reservation: r, selected, onPress }) => {
  const { rpx } = useResponsiveContext();
  const customer = [r.customerName, r.customerPhone]
    .filter(Boolean)
    .join(' · ');
  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={onPress}
      style={{
        borderWidth: selected ? 2 : 1,
        borderColor: selected ? LD.brand : LD.hairline,
        borderRadius: rpx(12),
        paddingHorizontal: rpx(12),
        paddingVertical: rpx(10),
        marginTop: rpx(10),
        backgroundColor: LD.surface,
      }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography.Text size={rpx(15)} color={LD.ink} bold>
          {formatArrival(r.expectedArrivalAt)} 到店
        </Typography.Text>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          {selected ? (
            <CheckCircle2
              size={rpx(20)}
              color={LD.brand}
              style={{ marginRight: rpx(8) }}
            />
          ) : null}
          <StatusPill
            label={ReservationStatusLabel[r.status]}
            tone={toneFor(r.status)}
          />
        </View>
      </View>
      <DetailRow label="服务顾问" value={r.serviceAgentName} />
      <DetailRow label="业务类型" value={tagsOf(r.tags)} />
      <DetailRow label="预约项目" value={r.expectedItems ?? ''} />
      <DetailRow label="客户" value={customer} />
    </TouchableOpacity>
  );
});

export const ReservationAssociationSheet: FC<{
  reservations: Reservation[];
  onSelect: (id: string) => void;
  onClose: () => void;
}> = memo(({ reservations, onSelect, onClose }) => {
  const { rpx } = useResponsiveContext();
  const visible = reservations.length > 0;
  const [selectedId, setSelectedId] = useState<string | undefined>(undefined);

  // Reset the pending selection whenever the sheet closes / its candidates change.
  useEffect(() => {
    if (!visible) setSelectedId(undefined);
  }, [visible]);

  const onConfirm = () => {
    if (selectedId) {
      onSelect(selectedId);
    } else {
      onClose();
    }
  };

  return (
    <BottomPanel visible={visible} onClose={onClose} fitContent>
      {/* Panel interior reads grey so the white cards stand out as choices. */}
      <View
        style={{
          backgroundColor: LD.canvas,
          paddingHorizontal: rpx(16),
          paddingTop: rpx(16),
          paddingBottom: rpx(8),
        }}
      >
        <Typography.Text size={rpx(17)} color={LD.ink} bold>
          关联预约
        </Typography.Text>
        <Typography.Text size={rpx(12)} color={LD.steel} mt={rpx(4)}>
          该车牌有未完成的预约，选择关联到本单（开单后将自动确认到店）
        </Typography.Text>
        {/* A plain View (not ScrollView): in a `fitContent` panel the host has
            no fixed height, so a ScrollView collapses; the panel caps at 80% and
            the dimmer/back still dismiss in the rare overflow case. A single
            vehicle realistically has only a few open reservations. */}
        <View>
          {reservations.map(r => (
            <ReservationCard
              key={r.id}
              reservation={r}
              selected={selectedId === r.id}
              onPress={() =>
                setSelectedId(prev => (prev === r.id ? undefined : r.id))
              }
            />
          ))}
        </View>
        <TouchableOpacity
          activeOpacity={0.7}
          onPress={onConfirm}
          style={{
            marginTop: rpx(14),
            marginBottom: rpx(8),
            height: rpx(46),
            borderRadius: rpx(12),
            backgroundColor: selectedId ? LD.brand : LD.hairline,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography.Text
            size={rpx(15)}
            color={selectedId ? LD.surface : LD.steel}
            bold={!!selectedId || undefined}
            medium
          >
            {selectedId ? '关联预约' : '不关联'}
          </Typography.Text>
        </TouchableOpacity>
      </View>
    </BottomPanel>
  );
});
